/**
 * /acompanhamento — Fase 5 — Painel consolidado de pedidos por etapa.
 *
 * Responde "onde cada pedido esta agora?" sem trip por multiplas paginas
 * (Liberacao / Medicao / Executivo / PPCP / Marcenaria / etc).
 *
 * Layout 3 zonas:
 *   1. EntityHeader (titulo + refresh)
 *   2. KpiStrip sticky (totais por etapa + atrasados + urgentes)
 *   3. Filtros chips + busca + tabela master + SlideOverPanel drill-down
 *
 * Fontes hibridas (decisao da usuaria):
 *   - PLANILHA do Gerenciador via useDados()
 *   - BACKEND Pedido via listarPedidos() (so RECEBIDO/EM_PRODUCAO/etc; ignora CANCELADO)
 *   Sem dedup automatico — origem visivel via badge.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useDados } from "../../context/DadosContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import { listarPedidos } from "../../services/pedidos";
import { mq } from "../../styles/canonical/breakpoints";
import { PageContainer } from "../../styles/shared";

import DrillPanel from "./DrillPanel";
import ListaCards from "./ListaCards";
import {
  consolidarAcompanhamento,
  contarPorBucket,
  ETAPA,
  ETAPA_LABEL,
  ETAPA_TONE,
  PRIORIDADE_TONE,
} from "./classificador";
import { atrasoTone, fmtAtraso, fmtDias } from "./helpers";
import {
  Card,
  ChipBar,
  ChipCount,
  EmptyMsg,
  ErrorMsg,
  FilterChip,
  FiltroLinha,
  OcultarEntregues,
  OriginBadge,
  Pill,
  PrioridadeMini,
  SearchInput,
  SecondaryButton,
  SubText,
  Table,
  TableWrapper,
  TruncCell,
  BotaoIcone,
  CabecalhoCompacto,
  KpiFaixa,
  KpiTile,
  TituloCompacto,
} from "./styles";

const CHIPS_RAPIDOS = [
  { id: "tudo", label: "Tudo", tone: "info" },
  { id: "atrasados", label: "Atrasados", tone: "danger" },
  { id: "urgentes", label: "Urgentes", tone: "warning" },
  { id: ETAPA.AGUARDANDO_LIBERACAO, label: ETAPA_LABEL[ETAPA.AGUARDANDO_LIBERACAO], tone: "neutral" },
  { id: ETAPA.EM_MEDICAO, label: ETAPA_LABEL[ETAPA.EM_MEDICAO], tone: "info" },
  { id: ETAPA.EM_EXECUTIVO, label: ETAPA_LABEL[ETAPA.EM_EXECUTIVO], tone: "info" },
  { id: ETAPA.EM_REVISAO, label: ETAPA_LABEL[ETAPA.EM_REVISAO], tone: "info" },
  { id: ETAPA.AGUARDANDO_APROVACAO, label: ETAPA_LABEL[ETAPA.AGUARDANDO_APROVACAO], tone: "warning" },
  { id: ETAPA.EM_PRODUCAO, label: ETAPA_LABEL[ETAPA.EM_PRODUCAO], tone: "info" },
  { id: ETAPA.EM_EXPEDICAO, label: ETAPA_LABEL[ETAPA.EM_EXPEDICAO], tone: "info" },
];

function Acompanhamento() {
  const navigate = useNavigate();
  const compacto = useMediaQuery(mq.tablet);
  const { request } = useAuth();
  const { dados: dadosPlanilha, erro: erroPlanilha, loading: planilhaLoading } = useDados();

  const [pedidosBackend, setPedidosBackend] = useState([]);
  const [loadingBackend, setLoadingBackend] = useState(true);
  const [erroBackend, setErroBackend] = useState(null);
  const [filtro, setFiltro] = useState("tudo");
  const [busca, setBusca] = useState("");
  const [selecionado, setSelecionado] = useState(null);
  const [excluirEntregues, setExcluirEntregues] = useState(true);

  // ============== Carrega backend ==============

  const carregarBackend = useCallback(async () => {
    setLoadingBackend(true);
    setErroBackend(null);
    try {
      // pageSize alto — a cliente-piloto tem poucos Pedidos backend hoje (a maioria
      // ainda esta na planilha). Quando crescer, paginar.
      const payload = await listarPedidos(request, { pageSize: 200 });
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setPedidosBackend(items);
    } catch (err) {
      setErroBackend(err?.message || "Erro ao carregar pedidos.");
      setPedidosBackend([]);
    } finally {
      setLoadingBackend(false);
    }
  }, [request]);

  useEffect(() => {
    carregarBackend();
  }, [carregarBackend]);

  // ============== Consolida + filtra ==============

  const todos = useMemo(
    () => consolidarAcompanhamento({
      dadosPlanilha: dadosPlanilha || [],
      pedidosBackend,
    }),
    [dadosPlanilha, pedidosBackend],
  );

  const visiveis = useMemo(() => {
    let xs = todos;
    if (excluirEntregues) {
      xs = xs.filter((it) => it.etapa !== ETAPA.ENTREGUE);
    }
    // Filtro chip
    if (filtro === "atrasados") {
      xs = xs.filter((it) => it.atrasoDias !== null && it.atrasoDias > 0);
    } else if (filtro === "urgentes") {
      xs = xs.filter((it) => it.prioridade === "URGENTE" || it.prioridade === "EXPRESS");
    } else if (filtro !== "tudo") {
      xs = xs.filter((it) => it.etapa === filtro);
    }
    // Busca
    const q = busca.trim().toLowerCase();
    if (q) {
      xs = xs.filter((it) =>
        String(it.cliente).toLowerCase().includes(q) ||
        String(it.ambiente).toLowerCase().includes(q) ||
        String(it.numeroProjeto).toLowerCase().includes(q) ||
        String(it.numeroPedido || "").toLowerCase().includes(q)
      );
    }
    return xs;
  }, [todos, filtro, busca, excluirEntregues]);

  const counts = useMemo(() => {
    const base = excluirEntregues
      ? todos.filter((it) => it.etapa !== ETAPA.ENTREGUE)
      : todos;
    return contarPorBucket(base);
  }, [todos, excluirEntregues]);

  // ============== KPI strip ==============

  const kpis = useMemo(() => {
    return [
      { label: "Total ativos", value: counts.total, tone: "info" },
      { label: "Em projeto exec", value: counts.porEtapa[ETAPA.EM_EXECUTIVO] + counts.porEtapa[ETAPA.EM_REVISAO] + counts.porEtapa[ETAPA.AGUARDANDO_APROVACAO], tone: "info" },
      { label: "Em produção", value: counts.porEtapa[ETAPA.EM_PRODUCAO], tone: "info" },
      { label: "Em expedição", value: counts.porEtapa[ETAPA.EM_EXPEDICAO], tone: "info" },
      { label: "Atrasados", value: counts.atrasados, tone: counts.atrasados > 0 ? "danger" : "neutral" },
      { label: "Urgentes", value: counts.urgentes, tone: counts.urgentes > 0 ? "warning" : "neutral" },
    ];
  }, [counts]);

  const loading = planilhaLoading || loadingBackend;
  const planilhaAusente = !planilhaLoading && (!dadosPlanilha || dadosPlanilha.length === 0);

  // ============== Render ==============

  return (
    <PageContainer>
      {compacto ? (
        <>
          <CabecalhoCompacto>
            <TituloCompacto>
              Acompanhamento de pedidos
              <small>
                {visiveis.length} de {counts.total} pedidos{excluirEntregues ? " (ativos)" : ""}
              </small>
            </TituloCompacto>
            <BotaoIcone type="button" onClick={carregarBackend} title="Atualizar" aria-label="Atualizar">
              <FiRefreshCw size={15} aria-hidden="true" />
            </BotaoIcone>
          </CabecalhoCompacto>
          <KpiFaixa role="group" aria-label="Indicadores">
            {kpis.map((kpi) => (
              <KpiTile key={kpi.label} $tone={kpi.tone}>
                <span className="kpi-label">{kpi.label}</span>
                <span className="kpi-num">{kpi.value}</span>
              </KpiTile>
            ))}
          </KpiFaixa>
        </>
      ) : (
        <>
          <EntityHeader
            title="Acompanhamento de pedidos"
            subtitle={`${visiveis.length} de ${counts.total} pedidos${excluirEntregues ? " (ativos)" : ""}`}
            primaryAction={{
              label: "Atualizar",
              icon: <FiRefreshCw size={14} aria-hidden="true" />,
              onClick: carregarBackend,
            }}
          />

          <KpiStrip items={kpis} />
        </>
      )}

      {!loading && planilhaAusente && pedidosBackend.length === 0 && (
        <ErrorMsg>
          <FiAlertCircle size={14} aria-hidden="true" />
          {" "}Nenhuma planilha do Gerenciador carregada e nenhum pedido no backend.
          {" "}Carregue a planilha em <strong>/configuracoes</strong> ou crie pedidos via <strong>/orcamentos</strong>.
        </ErrorMsg>
      )}

      {erroBackend && (
        <ErrorMsg>
          <FiAlertCircle size={14} aria-hidden="true" />
          {" "}Erro ao carregar pedidos do backend: {erroBackend}
        </ErrorMsg>
      )}

      {erroPlanilha && (
        <ErrorMsg>
          <FiAlertCircle size={14} aria-hidden="true" />
          {" "}Erro ao carregar planilha: {erroPlanilha}
        </ErrorMsg>
      )}

      <Card>
        <FiltroLinha>
          <SearchInput
            type="search"
            placeholder="Buscar por cliente, ambiente ou número..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <OcultarEntregues>
            <input
              type="checkbox"
              checked={excluirEntregues}
              onChange={(e) => setExcluirEntregues(e.target.checked)}
            />
            Ocultar entregues
          </OcultarEntregues>
        </FiltroLinha>

        <ChipBar>
          {CHIPS_RAPIDOS.map((chip) => {
            const count = chip.id === "tudo"
              ? counts.total
              : chip.id === "atrasados"
                ? counts.atrasados
                : chip.id === "urgentes"
                  ? counts.urgentes
                  : counts.porEtapa[chip.id] || 0;
            return (
              <FilterChip
                key={chip.id}
                type="button"
                $active={filtro === chip.id}
                $tone={chip.tone}
                onClick={() => setFiltro(chip.id)}
                title={`Mostrar: ${chip.label}`}
              >
                {chip.label}
                <ChipCount>{count}</ChipCount>
              </FilterChip>
            );
          })}
        </ChipBar>

        {loading ? (
          <EmptyMsg>Carregando dados...</EmptyMsg>
        ) : visiveis.length === 0 ? (
          <EmptyMsg>
            Nenhum item para os filtros aplicados.
            {filtro !== "tudo" && (
              <>
                {" "}
                <SecondaryButton type="button" onClick={() => setFiltro("tudo")}>
                  Limpar filtro
                </SecondaryButton>
              </>
            )}
          </EmptyMsg>
        ) : compacto ? (
          <ListaCards itens={visiveis} onAbrir={setSelecionado} />
        ) : (
          <TableWrapper>
            <Table $rowsClickable>
              <colgroup>
                <col style={{ width: "8%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Origem</th>
                  <th>Número</th>
                  <th>Cliente</th>
                  <th>Ambiente</th>
                  <th>Etapa</th>
                  <th>Dias na etapa</th>
                  <th>Prazo</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setSelecionado(it)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <OriginBadge title={`Origem: ${it.origem}`}>
                        {it.origem === "backend" ? "ERP" : "Planilha"}
                      </OriginBadge>
                    </td>
                    <td title={it.numeroProjeto}>
                      <TruncCell>{it.numeroProjeto}</TruncCell>
                      {it.prioridade && it.prioridade !== "NORMAL" && (
                        <SubText>
                          <PrioridadeMini>
                            <Pill $tone={PRIORIDADE_TONE[it.prioridade] || "neutral"}>
                              {it.prioridade}
                            </Pill>
                          </PrioridadeMini>
                        </SubText>
                      )}
                    </td>
                    <td title={it.cliente}>
                      <TruncCell>{it.cliente}</TruncCell>
                    </td>
                    <td title={it.ambiente}>
                      <TruncCell>{it.ambiente}</TruncCell>
                    </td>
                    <td>
                      <Pill $tone={ETAPA_TONE[it.etapa]}>{ETAPA_LABEL[it.etapa]}</Pill>
                    </td>
                    <td>{fmtDias(it.diasNaEtapa)}</td>
                    <td>
                      {it.atrasoDias !== null ? (
                        <Pill $tone={atrasoTone(it.atrasoDias)}>{fmtAtraso(it.atrasoDias)}</Pill>
                      ) : (
                        <SubText>—</SubText>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <SlideOverPanel
        open={Boolean(selecionado)}
        onClose={() => setSelecionado(null)}
        title={selecionado ? `${selecionado.numeroProjeto} — ${selecionado.ambiente}` : ""}
        subtitle={selecionado?.cliente}
        width="min(90vw, 560px)"
      >
        {selecionado && (
          <DrillPanel item={selecionado} onNavigate={(rota) => {
            setSelecionado(null);
            navigate(rota);
          }} />
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}

export default Acompanhamento;
